import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Col, Form, Select, Tag } from 'antd';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import {
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  Dropdown,
  Row,
  FormField,
  DatePicker,
  InfoTooltip,
} from 'ui';
import {
  fetchWipCodesForScope,
  fetchWipCodesForOrder,
  saveWipCodesOnOrder,
} from '../../../settings/containers/wip-codes/wip-codes-actions';

const StyledDatePicker = styled(DatePicker)`
  height: 33px;
  width: 100%;
`;

const WipCodes = ({
  intl,
  orderId,
  isDisabled,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const {
    wipCodesForScope,
    wipCodesFromOrder,
  } = useSelector(state => state.wipCodes);

  const [orderWipCodes, setOrderWipCodes] = useState([]);

  useEffect(() => {
    if (orderId) {
      dispatch(fetchWipCodesForScope(orderId));
      dispatch(fetchWipCodesForOrder(orderId));
    }
  }, [orderId]);

  useDeepCompareEffect(() => {
    if (wipCodesFromOrder) {
      form.setFieldsValue({
        wipCodeIds: wipCodesFromOrder?.wipCodeIds,
        assignedDateUtc: wipCodesFromOrder?.assignedDateUtc ?
          moment(wipCodesFromOrder?.assignedDateUtc) : null,
      });
      setOrderWipCodes(wipCodesFromOrder);
    }
  }, [wipCodesFromOrder]);

  const handleAutoSave = () => {
    dispatch(saveWipCodesOnOrder({ orderId, ...form.getFieldsValue() }));
  };

  const wipCodeInfo = orderWipCodes?.wipCodeIds?.map((w) => {
    const currentWipCode = wipCodesForScope?.find(wipCode => wipCode.id === w);
    if (currentWipCode) {
      return `
        <div style="margin-bottom: 15px;" key=${currentWipCode.id}>
          <b>${currentWipCode.code} - ${currentWipCode.description}</b>
          <div>
            ${intl.formatMessage({ id: 'wipCodes.ACTION' })}: ${currentWipCode.action || ''}
          </div>
        </div>
      `;
    }
    return '';
  }).join('');

  const tagRender = (tagProps) => {
    const { label, closable, onClose, disabled } = tagProps;
    return (
      <Tag
        // Add possibility to close disabled tags
        closable={disabled ? !isDisabled : closable}
        onClose={onClose}
        icon={disabled && !isDisabled && <ExclamationCircleOutlined />}
        color={disabled && !isDisabled && 'error'}
      >
        {label}
      </Tag>
    );
  };

  return <Form
    layout='vertical'
    requiredMark={false}
    form={form}
  >
    <Row type='flex' gutter={16} marginRight={0}>
      <Col lg={16} md={16} sm={12} xs={24}>
        <FormField
          name='wipCodeIds'
          label={
            <>
              <span>{intl.formatMessage({ id: 'wipCodes.WIP_CODES' })}</span>
              {wipCodesForScope?.length > 0 &&
                <InfoTooltip
                  content={wipCodeInfo?.length ? wipCodeInfo : ''}
                  placement='rightBottom'
                />
              }
            </>
          }
        >
          <Dropdown
            tagRender={tagRender}
            multiSelect
            showArrow={true}
            onChange={() => handleAutoSave()}
            values={wipCodesForScope}
            value={orderWipCodes?.wipCodeIds}
            optionLabelProp='title'
            disabled={isDisabled}
            renderOption={v =>
              <Select.Option
                value={v.id}
                key={v.id}
                disabled={v.isDeleted}
                title={v.code}
              >
                {v.code} - {v.description}
              </Select.Option>
            }
          />
        </FormField>
      </Col>
      <Col lg={8} md={8} sm={12} xs={24}>
        <FormField
          name='assignedDateUtc'
          label={intl.formatMessage({ id: 'wipCodes.WIP_CODE_DATE' })}
        >
          <StyledDatePicker
            onChange={() => handleAutoSave()}
            disabled={isDisabled}
            allowPast={false}
          />
        </FormField>
      </Col>
    </Row>
  </Form>;
};

WipCodes.propTypes = {
  intl: PropTypes.object,
  orderId: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default injectIntl(WipCodes);
